/**
 * Windows system — typy + persistence shape.
 *
 * **Model:**
 *   - `UnpublishedWindow` = okno zdefiniowane (registry) z metadata o tym
 *     do których areas można je przeciągać (`allowedAreas`).
 *   - `WindowsLayoutState` = mapowanie area -> uporządkowana lista windowId.
 *     Kolejność listy = kolejność renderu w obszarze.
 *
 * **Faza G (now):** mock unpublished windows + cookie persistence dla layout state.
 * **Faza F.2+:** real `imp_window` + `imp_cms_catalog_window` przez REST API.
 *
 * @see ../../.claude/CLAUDE.md sekcja "Architektura layoutów"
 */

import type { AreaSymbol } from "@/lib/layouts/types";

export type UnpublishedWindow = {
  id: number;
  /** Nazwa instancji okna (admin-defined w `imp_cms_window.name`, np. "Menu główne"). */
  name: string;
  /** Symbol komponentu backend (snake_case, np. `w_menu`) — discriminator dla filter dropdown. */
  componentSymbol: string;
  /** Label typu komponentu per-locale (`{pl: "Menu główne", en: "Main menu"}`) — secondary text + dropdown label. */
  componentName: Record<string, string>;
  /** Lista area symboli do których to okno może być przeciągnięte. Pusty array = nigdzie. */
  allowedAreas: AreaSymbol[];
  /** Query-string'i do IADMIN dla "Konfiguracja okna" + "Zarządzaj treścią okna"
      (zwracane przez backend `IAPI_windows` / `IAPI_unpublishedWindows`). */
  adminLinks: WindowAdminLinks;
  /** Notatki administratora z `imp_cms_window.text` — tekst opisowy dla
      pozostałych adminów (np. "Menu główne klienta — nie modyfikuj bez zgody").
      `null` = pole puste -> React-CMS pokazuje zablokowaną ikonę info z hint'em
      "uzupełnij w Imperial Administrator > Windows Manager > Edytuj okno". */
  adminNotes?: string | null;
  /** Obraz/kolor tła kontenera okna — patrz `CmsBackground` w `lib/api/cms-windows`.
      `null` gdy admin nie wybrał lub moduł `m_site_backgrounds` wyłączony. */
  background?: import("@/lib/api/cms-windows").CmsBackground | null;
  /** Symbol kontenera (wrapper HTML) z DB `imp_cms_container.symbol`. Backend
      lookup 2-warstwowy (V1 per-instance + V1 default per komponent). `null` gdy
      brak konfiguracji - `WindowRenderer` używa hardcoded fallback'u w lambda. */
  containerSymbol?: string | null;
};

/** Single source of truth dla shape'u admin_links — używany przez adaptery
    i komponenty UI. Reeksport z `lib/api/cms-windows` dla wygody importu. */
export type WindowAdminLinks = {
  configUrl: string;
  contentUrl: string | null;
  contentBlockedReason: string | null;
};

/** Mapping area -> uporządkowana lista windowId aktualnie w tym obszarze. */
export type WindowsLayoutState = {
  publishedByArea: Partial<Record<AreaSymbol, number[]>>;
};

export const COOKIE_WINDOWS_LAYOUT = "cms_windows_layout";

/**
 * Cookie z mapą per-slug windows state — `{ slug -> WindowsLayoutState }`.
 *
 * **Faza G.9 zmiana:** zastępuje single `cms_windows_layout` cookie. Każda
 * podstrona ma teraz osobny windows state. Migration: stare cookie ignorowane.
 */
export const COOKIE_WINDOWS = "cms_windows";

export type WindowsBySlug = Record<string, WindowsLayoutState>;

export const EMPTY_WINDOWS_STATE: WindowsLayoutState = {
  publishedByArea: {},
};

export const EMPTY_WINDOWS_BY_SLUG: WindowsBySlug = {};

export function isWindowsBySlug(value: unknown): value is WindowsBySlug {
  if (typeof value !== "object" || value === null) return false;
  for (const [slug, state] of Object.entries(value as Record<string, unknown>)) {
    if (typeof slug !== "string") return false;
    if (!isWindowsLayoutState(state)) return false;
  }
  return true;
}

/** Type guard — sprawdza czy parsed JSON ma valid shape. */
export function isWindowsLayoutState(value: unknown): value is WindowsLayoutState {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.publishedByArea !== "object" || v.publishedByArea === null) return false;
  for (const ids of Object.values(v.publishedByArea as Record<string, unknown>)) {
    if (!Array.isArray(ids) || ids.some((x) => typeof x !== "number")) return false;
  }
  return true;
}

/** Helper: pobierz windowIds dla danego area (zawsze array, nigdy undefined). */
export function getWindowIdsForArea(
  state: WindowsLayoutState,
  area: AreaSymbol,
): number[] {
  return state.publishedByArea[area] ?? [];
}

/** Helper: zwraca area w której jest windowId, lub null gdy nieopublikowane. */
export function findAreaForWindowId(
  state: WindowsLayoutState,
  windowId: number,
): AreaSymbol | null {
  for (const [area, ids] of Object.entries(state.publishedByArea)) {
    if (ids?.includes(windowId)) return area as AreaSymbol;
  }
  return null;
}
